import { useMemo, forwardRef } from 'react';
import cn from "../../../utils/cn";
import PropTypes from 'prop-types';
import Icon from "../Icon/Icon";
import getColorLuma from "../../../utils/colors/getColorLuma";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Badge = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    className,
    color,
    text,
    badgeAriaLabel = 'Badge',
    dataTestId,
    shape = 'circle',
    onClick,
    type,
    icon
  } = props;
  const luma = getColorLuma(color);
  const textColor = luma < 128 ? 'white' : '#4A657F';
  const style = {
    color: textColor,
    backgroundColor: props.color,
    ...props.style
  };
  const badgeBaseClass = 'min-w-6 text-center box-border inline-block text-xs p-1';
  const badgeClass = useMemo(() => {
    const baseClass = shape === 'square' ? 'leading-7 h-10 rounded-none font-heavy-font px-2' : 'leading-3 h-6 rounded-xl';
    return cn(badgeBaseClass, baseClass, {
      'border-solid border border-brand-blue rounded-6 h-6': type === 'filter'
    }, className);
  }, [shape, className, type]);
  if (text === '' || text === undefined) {
    return null;
  }
  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClick) {
        onClick(e);
      }
    } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };
  const iconElement = icon?.name ? /*#__PURE__*/_jsx(Icon, {
    color: icon?.color,
    icon: icon.name,
    style: icon?.style,
    className: icon?.className
  }) : null;
  // Render as a button only if onClick is present
  if (onClick) {
    return /*#__PURE__*/_jsxs("button", {
      id: id,
      type: "button",
      className: badgeClass,
      style: style,
      tabIndex: 0,
      ref: ref,
      onKeyDown: handleKeyDown,
      "data-testid": dataTestId,
      onClick: onClick,
      children: [iconElement && icon?.position === 'left' && /*#__PURE__*/_jsx("span", {
        className: "mr-1",
        children: iconElement
      }), text, iconElement && icon?.position === 'right' && /*#__PURE__*/_jsx("span", {
        className: "ml-1",
        children: iconElement
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    id: id,
    className: badgeClass,
    style: style,
    "aria-label": `${badgeAriaLabel}: ${text}`,
    tabIndex: 0,
    ref: ref,
    onKeyDown: handleKeyDown,
    "data-testid": dataTestId,
    children: [iconElement && icon?.position === 'left' && /*#__PURE__*/_jsx("span", {
      className: "mr-1",
      children: iconElement
    }), text, iconElement && icon?.position === 'right' && /*#__PURE__*/_jsx("span", {
      className: "ml-1",
      children: iconElement
    })]
  });
});
Badge.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  text: PropTypes.string,
  badgeAriaLabel: PropTypes.string,
  dataTestId: PropTypes.string,
  shape: PropTypes.oneOf(['square', 'circle']),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['filter']),
  icon: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
    style: PropTypes.string,
    position: PropTypes.oneOf(['left', 'right']),
    className: PropTypes.string
  }),
  style: PropTypes.shape({
    height: PropTypes.num,
    fontSize: PropTypes.string,
    backgroundColor: PropTypes.string,
    paddingTop: PropTypes.num,
    wordBreak: PropTypes.string,
    minWidth: PropTypes.num,
    textTransform: PropTypes.string
  })
};
Badge.displayName = 'Badge';
export default Badge;
//# sourceMappingURL=Badge.js.map